/**
 * Axios API client with JWT auth, tenant-aware headers, and envelope unwrapping.
 */
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { holdSettled } from '@offline/hold'
import { useAuthStore } from '@store/authStore'
import { getPortalContext } from '@utils/portalContext'
import toast from 'react-hot-toast'
import { useConnectionStore } from '@store/connectionStore'
import { parseError } from '@utils/errors'
import { tenantInactiveMessage, useTenantAccess } from './tenantAccess'

// Lets a caller opt a specific request out of the global error toast below --
// for a background call whose failure is expected/harmless for some roles
// (e.g. a header logo fetch gated to ops roles), a 403 popup is just noise,
// not a real "you tried to do something you can't" moment worth interrupting
// the user for.
declare module "axios" {
  export interface AxiosRequestConfig {
    suppressErrorToast?: boolean
  }
}

// Standard API envelope from backend
export interface ApiResponse<T = unknown> {
  success: boolean
  data: T
  message: string
  errors: Record<string, string[]> | null
  meta: {
    page?: number
    total_count?: number
    total_pages?: number
    timestamp?: string
    [key: string]: unknown
  }
}

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': localStorage.getItem('kvbms_language') || 'en',
  },
})

// Request interceptor: attach JWT + tenant slug
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Derive tenant slug from the current hostname (ground truth).
    // Using the hostname means the login request also carries the slug before
    // the user is authenticated, and cross-subdomain access is naturally blocked
    // by TenantSchemaMiddleware (JWT tenant_schema won't match the hostname slug).
    const { isTenantPortal, tenantSlug: hostnameSlug } = getPortalContext()
    if (isTenantPortal && hostnameSlug && config.url && !config.url.startsWith('/platform')) {
      config.headers['X-Tenant-Slug'] = hostnameSlug
    }

    // Language header
    const lang = localStorage.getItem('kvbms_language') || 'en'
    config.headers['Accept-Language'] = lang

    return config
  },
  (error) => Promise.reject(error)
)

// Shared refresh promise — prevents multiple concurrent requests from each
// independently calling token/refresh/ when the access token expires.
let _refreshPromise: Promise<string> | null = null

export function _doRefresh(): Promise<string> {
  if (_refreshPromise) return _refreshPromise
  _refreshPromise = (async () => {
    const refreshToken = useAuthStore.getState().refreshToken
    if (!refreshToken) throw new Error('No refresh token')
    const res = await axios.post('/api/v1/auth/token/refresh/', { refresh: refreshToken })
    // The server hands out a new refresh token each time and retires the old one (rotation). Keep the new one:
    // holding on to the old one made the second refresh fail, which signed people out after about two hours.
    const { access, refresh } = res.data
    useAuthStore.getState().setTokens(access, refresh ?? refreshToken)
    return access
  })().finally(() => { _refreshPromise = null })
  return _refreshPromise
}

/** The session cannot be saved: sign out and go to the sign-in page, which brings the person back to this page afterwards. */
export function endSession() {
  useAuthStore.getState().logout() // also starts writing the hold for entries waiting on this device (offline/hold.ts)
  // Wait for that write, but not long: on failure or timeout a synchronous fallback marker already blocks replay.
  void Promise.race([holdSettled(), new Promise((r) => setTimeout(r, 1500))]).finally(leave)
}
function leave() {
  const here = window.location.pathname + window.location.search
  const next = here.startsWith('/login') ? '' : `&next=${encodeURIComponent(here)}`
  window.location.href = `/login?expired=1${next}`
}

// Response interceptor: handle token refresh and error normalisation
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    useConnectionStore.getState().setProblem(null)
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // No answer at all, or the gateway in front of the API is failing: show the connection banner
    // instead of a toast per request.
    const status = error.response?.status
    const unreachable = !error.response || status === 502 || status === 503 || status === 504
    if (unreachable && error.code !== 'ERR_CANCELED') {
      useConnectionStore.getState().setProblem(navigator.onLine ? 'server' : 'offline')
    }

    // A wrong password (or a bad reset link) also answers 401, but there is no session to refresh and
    // nothing to sign out of: let the form show the message instead of reloading the page.
    const isSignInCall = /\/auth\/(login|password-reset|2fa)\//.test(originalRequest?.url ?? '')

    if (error.response?.status === 401 && !originalRequest._retry && !isSignInCall) {
      originalRequest._retry = true
      try {
        const access = await _doRefresh()
        originalRequest.headers.Authorization = `Bearer ${access}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Only a refused refresh ends the session; a dropped network or a failing gateway keeps it for the next try.
        const e = refreshError as { response?: { status?: number }; message?: string }
        if (e.message === 'No refresh token' || e.response?.status === 400 || e.response?.status === 401) endSession()
        return Promise.reject(error)
      }
    }

    // Replace Axios's "Request failed with status code NNN" and the backend's technical text with a
    // plain sentence, so every `toast.error(err.message)` across the app reads well. Form code can
    // call parseError(err) for the per-field messages.
    const friendly = parseError(error)
    error.message = friendly.message

    // The company is not active (or still being set up): one full-page notice instead of a toast per request. The sign-in
    // form shows the same message inline, so a sign-in attempt only gets the message.
    const inactive = tenantInactiveMessage(error)
    if (inactive !== null) {
      if (!isSignInCall) useTenantAccess.getState().setInactive(inactive)
      return Promise.reject(error)
    }

    if ((friendly.kind === 'permission' || (friendly.kind === 'server' && !unreachable)) && !originalRequest?.suppressErrorToast) {
      toast.error(friendly.message, { id: friendly.kind })
    }

    return Promise.reject(error)
  }
)

export default apiClient
